import SEO from "@/components/SEO";
import { Reveal, Kicker, MaskedLines, UL, Marquee } from "@/components/Bits";
import { Bell, ShieldCheck, KeyRound, Navigation, Star } from "lucide-react";

const FEATURES = [
  "GPS-first search — the closest available verified helper, every time.",
  "OTP-secured start & finish on eligible categories.",
  "Live tracking with real-time ETA during the job.",
  "Ratings & reviews that keep quality high.",
  "One-tap complaint / dispute route with photo evidence.",
];

function StoreBadge({ store, line2, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex min-w-[220px] cursor-not-allowed items-center gap-4 border border-cream/20 bg-cream/5 px-6 py-4 opacity-80"
    >
      <Bell className="h-6 w-6 text-brand" strokeWidth={1.7} />
      <div className="text-left">
        <p className="text-[10px] uppercase tracking-[0.2em] text-cream/50">{store}</p>
        <p className="font-serif text-lg font-semibold text-cream">Coming Soon</p>
        <p className="text-[10px] text-cream/40">{line2}</p>
      </div>
    </div>
  );
}

export default function Download() {
  return (
    <div data-testid="download-page" className="bg-ink text-cream">
      <SEO
        title="Download the App"
        description="The Middle Man app is coming soon to Google Play and the App Store. Verified local help for everyday tasks — live tracking, OTP-secured jobs, fair pricing."
        path="/download"
      />
      <section className="border-b border-cream/10">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 pb-20 pt-32 md:pt-40 lg:grid-cols-2">
          <div>
            <Reveal><Kicker light>The Middle Man app</Kicker></Reveal>
            <MaskedLines
              className="mt-7 font-serif text-5xl font-semibold leading-[1.05] tracking-tight md:text-6xl"
              lines={["Help is on", <em key="l2" className="text-brand">its way.</em>]}
            />
            <Reveal delay={0.3}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-cream/65">
                The Middle Man mobile app is launching soon on Google Play and the App Store. Store links will appear
                here the moment we go live — until then, everything about the platform is right here on this website.
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="mt-9 flex flex-wrap gap-4" data-testid="store-badges">
                <StoreBadge store="Google Play" line2="Android" testid="badge-google-play" />
                <StoreBadge store="App Store" line2="iOS" testid="badge-app-store" />
              </div>
            </Reveal>
            <Reveal delay={0.5}>
              <p className="mt-8 text-sm text-cream/50">
                Want a heads-up at launch? Write to{" "}
                <a href="mailto:info.touristjunction@gmail.com" data-testid="download-notify-email" className="font-semibold text-brand">
                  info.touristjunction@gmail.com
                </a>{" "}
                or call +91 96070 14213.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="hidden justify-center lg:flex">
            <div className="border border-cream/15 bg-cream/5 p-10 shadow-[10px_10px_0_0_#E85A2C]">
              <img src="/middleman-logo.jpg" alt="Middle Man app logo" className="h-auto w-72 object-contain" />
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee dark items={["Verified helpers", "OTP-secured jobs", "Live tracking", "Fair pricing", "26 categories", "208 subcategories"]} />

      <section className="mx-auto max-w-7xl px-6 py-20 md:py-24">
        <Reveal><Kicker light>Inside the app</Kicker></Reveal>
        <div className="mt-10 grid gap-10 lg:grid-cols-2">
          <Reveal delay={0.05}>
            <div className="space-y-5">
              {[
                { Icon: ShieldCheck, t: "Verified Middle Men", d: "Aadhaar / PAN checks + address validation before activation." },
                { Icon: KeyRound, t: "OTP-secured jobs", d: "Work starts and finishes only after you share an OTP." },
                { Icon: Navigation, t: "Live tracking", d: "See your Middle Man on the map with real-time ETA." },
                { Icon: Star, t: "Ratings & reviews", d: "Quality stays high; suspicious accounts are suspended." },
              ].map((f) => (
                <div key={f.t} className="flex gap-5 border-b border-cream/10 pb-5">
                  <f.Icon className="mt-1 h-5 w-5 shrink-0 text-brand" strokeWidth={1.7} />
                  <div>
                    <h3 className="font-serif text-lg font-semibold">{f.t}</h3>
                    <p className="mt-1 text-sm text-cream/55">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="border border-cream/15 bg-cream/5 p-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand">While you wait</p>
              <div className="mt-6">
                <UL items={FEATURES} />
              </div>
              <p className="mt-8 text-sm leading-relaxed text-cream/55">
                Browse <a href="/services" className="font-semibold text-brand">all 26 service categories</a> and the{" "}
                <a href="/pricing" className="font-semibold text-brand">recommended charges</a> right here on the website.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
